import styled from 'styled-components';
import { statusCountAccent, statusCountSurface } from './statusCountColors';

export const DeliveryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;

  th,
  td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.gray?.[100] || '#eee'};
    vertical-align: middle;
  }

  th {
    background: ${({ theme }) => theme.colors?.background || '#f5f5f5'};
    font-weight: 600;
    color: ${({ theme }) => theme.colors?.gray?.[900] || '#222'};
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    &:hover {
      background: ${({ theme }) => theme.colors?.gray?.[100] || '#eee'};
    }
  }

  tbody tr.main-row {
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: ${({ theme }) => (theme.colors?.primary?.light ? `${theme.colors.primary.light}12` : 'rgba(94, 129, 172, 0.08)')};
    }
  }

`;

export const ExpandedBlock = styled.div`
  display: grid;
  gap: 8px;
  line-height: 1.5;
  word-break: break-word;

  strong {
    color: ${({ theme }) => theme.colors?.gray?.[900] || '#222'};
    margin-right: 6px;
  }
`;

export const ActionCell = styled.td`
  white-space: nowrap;
`;

export const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  margin: 0 2px;
  background: none;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors?.primary?.main || '#3b5998'};
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.colors?.primary?.light ? `${theme.colors.primary.light}25` : 'rgba(59, 89, 152, 0.15)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

export const ActionSeparator = styled.span`
  color: ${({ theme }) => theme.colors?.gray?.[300] || '#ccc'};
  font-weight: 300;
  margin: 0 4px;
  user-select: none;
`;

export const MainRow = styled.tr`
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;

  &:hover {
    background: ${({ theme }) => (theme.colors?.primary?.light ? `${theme.colors.primary.light}12` : 'rgba(94, 129, 172, 0.08)')};
  }

  ${({ $unviewed, theme }) =>
    $unviewed &&
    `
    background: ${theme?.colors?.primary?.light ? `${theme.colors.primary.light}08` : 'rgba(94, 129, 172, 0.05)'};
    border-left: 3px solid ${theme?.colors?.primary?.main || '#3b5998'};
    &:hover {
      background: ${theme?.colors?.primary?.light ? `${theme.colors.primary.light}14` : 'rgba(94, 129, 172, 0.1)'};
    }
  `}

  ${({ $disabled, theme }) =>
    $disabled &&
    `
    cursor: not-allowed;
    opacity: 0.68;
    color: ${theme?.colors?.gray?.[600] || '#6b7280'};
    background: ${theme?.colors?.gray?.[50] || '#f9fafb'};
    border-left: none;
    &:hover {
      background: ${theme?.colors?.gray?.[50] || '#f9fafb'};
    }
  `}
`;

export const ExpandedRow = styled.tr`
  td {
    background: ${({ theme }) => theme.colors?.gray?.[50] || '#fafafa'};
    padding: 16px 16px 20px clamp(16px, 5vw, 48px);
    border-bottom: 1px solid ${({ theme }) => theme.colors?.gray?.[100] || '#eee'};
    font-size: 14px;
    color: ${({ theme }) => theme.colors?.gray?.[700] || '#555'};
    word-break: break-word;
  }
`;

/** Abas da tela de entregas (incremental: novas abas podem ser adicionadas ao array de tabs). */
export const TabBar = styled.nav`
  display: flex;
  gap: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.gray?.[200] || '#e5e7eb'};
  margin-bottom: 20px;
`;

export const TabButton = styled.button`
  flex-shrink: 0;
  white-space: nowrap;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme, $active }) => ($active ? theme?.colors?.primary?.main || '#3b5998' : theme?.colors?.gray?.[600] || '#6b7280')};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ theme, $active }) => ($active ? theme?.colors?.primary?.main || '#3b5998' : 'transparent')};
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: ${({ theme }) => theme?.colors?.primary?.main || '#3b5998'};
  }
`;

export const TabContent = styled.div`
  flex: 1;
`;

/** Grupo de filtro para o multi-select de status (evita herdar width:100% dos checkboxes). */
export const StatusFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 12rem;
  max-width: 100%;
`;

export const StatusMultiSelectRoot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  width: 100%;
`;

export const StatusMultiSelectLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors?.gray?.[900] || '#333'};
`;

export const StatusMultiSelectTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors?.gray?.[200] || '#e0e0e0'};
  border-radius: 4px;
  width: 100%;
  min-height: 38px;
  background: #fff;
  font-size: 14px;
  color: #111827;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors?.primary?.main || '#3b5998'};
    outline: none;
  }
`;

export const StatusMultiSelectTriggerText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

export const StatusMultiSelectCaret = styled.span`
  color: #6b7280;
  font-size: 11px;
  flex-shrink: 0;
`;

export const StatusMultiSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  min-width: 100%;
  width: max(100%, 280px);
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors?.gray?.[200] || '#e0e0e0'};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
`;

export const StatusMultiSelectOption = styled.label`
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  padding: 8px 12px;
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: #111827;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;

  &:hover {
    background: ${({ theme }) => (theme.colors?.primary?.light ? `${theme.colors.primary.light}12` : 'rgba(94, 129, 172, 0.08)')};
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    min-width: 16px;
    max-width: 16px;
    margin: 0;
    padding: 0;
    border: none;
    cursor: pointer;
    justify-self: start;
  }

  span {
    text-align: left;
    word-break: break-word;
  }
`;

export const StatusMultiSelectFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 6px 12px 4px;
  border-top: 1px solid #f3f4f6;
`;

export const StatusMultiSelectClearButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors?.primary?.main || '#3b5998'};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const StatusCountsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

export const StatusCountsHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 16px;
`;

export const StatusCountsTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.gray?.[900] || '#222'};
`;

export const StatusCountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: 8px;
`;

export const StatusCountCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-height: 4.5rem;
  padding: 10px 12px 10px 14px;
  border-radius: 8px;
  border: 2px solid ${({ theme, $selected, $colorToken }) =>
    $selected ? statusCountAccent(theme, $colorToken) : theme.colors?.gray?.[200] || '#e0e0e0'};
  background: ${({ theme, $selected, $colorToken }) =>
    $selected ? statusCountSurface(theme, $colorToken) : '#fff'};
  cursor: pointer;
  text-align: left;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 3px;
    background: ${({ theme, $colorToken }) => statusCountAccent(theme, $colorToken)};
  }

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors?.primary?.main || '#3b5998'};
    outline-offset: 2px;
  }
`;

export const StatusCountLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors?.gray?.[700] || '#4B5563'};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const StatusCountValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors?.gray?.[900] || '#222'};
  font-variant-numeric: tabular-nums;
`;

export const StatusCountsError = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors?.danger?.dark || '#b91c1c'};
  font-size: 13px;
`;

export const DelayedFilter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 4px;
  flex: 0 0 auto;

  > span {
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors?.gray?.[900] || '#111827'};
  }
`;

export const FilterSwitch = styled.button`
  position: relative;
  width: 44px;
  height: 24px;
  margin: 7px 0;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: ${({ theme, $on }) => ($on ? theme.colors?.primary?.main || '#3B5998' : '#d1d5db')};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    transform: translateX(${({ $on }) => ($on ? '20px' : '0')});
    transition: transform 0.15s ease;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors?.primary?.main || '#3B5998'};
    outline-offset: 2px;
  }
`;

export const FilterActions = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
`;
